import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Loader2, BookOpen } from "lucide-react";

const BUSINESS_TYPES = [
  ["car_rental", "Car rental"],
  ["motorbike_rental", "Motorbike rental"],
  ["equipment_rental", "Equipment rental"],
  ["resort_hotel", "Resort / hotel"],
  ["event_rental", "Event rental"],
  ["other", "Other"],
];
const POSITIONS = [
  ["", "Not sure"],
  ["owner", "Owner"], ["manager", "Manager"], ["staff", "Staff"],
  ["reception", "Reception"], ["other", "Other"],
];
const RESULTS = [
  ["interested", "Interested"],
  ["not_interested", "Not interested"],
  ["maybe_later", "Maybe later"],
  ["wants_follow_up", "Wants follow-up"],
  ["already_signed_up", "Already signed up"],
  ["no_decision_maker", "No decision maker available"],
  ["need_to_return", "Need to return"],
  ["wrong_business", "Wrong business / not relevant"],
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const nowHM = () => new Date().toTimeString().slice(0, 5);

export default function PromoterNewLog() {
  const [params] = useSearchParams();
  const taskId = params.get("task_id") || "";
  const [task, setTask] = useState(null);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: "", business_type: "car_rental",
    business_address: "", city: "", province: "",
    contact_name: "", contact_position: "",
    contacted_date: todayIso(), contacted_time: nowHM(),
    business_phone: "", business_email: "",
    facebook_url: "", whatsapp_or_viber: "",
    result_status: "interested",
    notes: "", promoter_comments: "",
    next_follow_up_date: "",
    photo_urls: [],
    linked_task_id: taskId || undefined,
  });

  useEffect(() => {
    if (taskId) {
      api.get(`/promoter/tasks/${taskId}`).then(({ data }) => {
        setTask(data);
        setForm((f) => ({
          ...f, business_name: data.business_name || f.business_name,
          business_address: data.business_address || f.business_address,
          contact_name: data.contact_name || f.contact_name,
        }));
      }).catch(() => {});
    }
  }, [taskId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post(`/promoter/logs`, payload);
      toast.success("Visit log submitted.");
      // If we came from a task, auto-mark it completed.
      if (taskId) {
        await api.post(`/promoter/tasks/${taskId}/status`,
                       { status: "completed", completed_log_id: data.id }).catch(() => {});
      }
      navigate("/promoter/logs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="promoter-new-log-page">
      <h1 className="font-display text-2xl font-semibold">New Business Visit Log</h1>
      <p className="text-sm text-slate-600 mt-1">
        Fill what you know: only business name is required. You can polish details later.
      </p>
      <Link to="/promoter/guide"
             data-testid="new-log-open-guide"
             className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-800 hover:underline">
        <BookOpen className="w-3.5 h-3.5" /> Need help with the conversation? Open Sales Guide
      </Link>
      {task && (
        <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="prom-linked-task">
          Submitting result for assigned visit: <strong>{task.business_name}</strong> (priority {task.priority}).
        </div>
      )}
      <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="prom-new-log-form">
        <Row label="Business name *">
          <input data-testid="log-business-name" required value={form.business_name}
                 onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Business type">
          <select data-testid="log-business-type" value={form.business_type}
                  onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-slate-300">
            {BUSINESS_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row label="Address / location" wide>
          <input value={form.business_address}
                 onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="City / municipality">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Province">
          <input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Person contacted">
          <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Position">
          <select value={form.contact_position}
                  onChange={(e) => setForm({ ...form, contact_position: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-slate-300">
            {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row label="Date contacted">
          <input type="date" value={form.contacted_date}
                 onChange={(e) => setForm({ ...form, contacted_date: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Time contacted">
          <input type="time" value={form.contacted_time}
                 onChange={(e) => setForm({ ...form, contacted_time: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Business phone">
          <input value={form.business_phone}
                 onChange={(e) => setForm({ ...form, business_phone: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Business email">
          <input type="email" value={form.business_email}
                 onChange={(e) => setForm({ ...form, business_email: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Facebook page / Messenger">
          <input value={form.facebook_url}
                 onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="WhatsApp / Viber / other">
          <input value={form.whatsapp_or_viber}
                 onChange={(e) => setForm({ ...form, whatsapp_or_viber: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Result / status">
          <select data-testid="log-result-status" value={form.result_status}
                  onChange={(e) => setForm({ ...form, result_status: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-slate-300">
            {RESULTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
        <Row label="Next follow-up date">
          <input type="date" value={form.next_follow_up_date}
                 onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })}
                 className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Notes (what was discussed)" wide>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Promoter's own comments" wide>
          <textarea rows={2} value={form.promoter_comments}
                    onChange={(e) => setForm({ ...form, promoter_comments: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-slate-300" />
        </Row>
        <Row label="Photos / evidence (coming soon)" wide>
          <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded p-3">
            Photo uploads will be enabled in a future update. For now, capture them on your phone and keep them for your records.
          </div>
        </Row>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button onClick={() => navigate(-1)} className="px-3 py-2 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="log-submit-btn" disabled={submitting || !form.business_name}
                onClick={submit}
                className="px-4 py-2 text-sm rounded bg-[#1B4332] text-white inline-flex items-center gap-1 disabled:opacity-60">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Submit log
        </button>
      </div>
    </div>
  );
}

function Row({ label, wide, children }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-xs text-slate-600 font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
